import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Paper,
  TextField,
  Typography,
  Button,
  Divider,
  Portal,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import {
  Clear as ClearIcon,
  Close as CloseIcon,
} from '@mui/icons-material';

export interface FilterConfig {
  column: string;
  type: 'date' | 'text';
  value: string;
  startDate?: string;
  endDate?: string;
}

interface FilterSelectorProps {
  columns: Array<{ key: string; label: string }>;
  onFiltersChange: (filters: FilterConfig[]) => void;
  open: boolean;
  onClose: () => void;
  anchorEl?: HTMLElement | null;
}

const FilterSelector: React.FC<FilterSelectorProps> = ({
  columns,
  onFiltersChange,
  open,
  onClose,
  anchorEl,
}) => {
  const [filters, setFilters] = useState<FilterConfig[]>([]);
  const [selectedColumn, setSelectedColumn] = useState('');
  const [filterType, setFilterType] = useState<'date' | 'text'>('text');
  const [textValue, setTextValue] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const selectorRef = useRef<HTMLDivElement>(null);

  // Calculate position when component opens
  useEffect(() => {
    if (open && anchorEl) {
      const calculatePosition = () => {
        if (!anchorEl) return { top: 100, left: 100 };
        
        // Get the button's position relative to the viewport
        const rect = anchorEl.getBoundingClientRect();
        
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;
        const selectorWidth = 320;
        const selectorHeight = 400; // Reduced height for better positioning
        
        // Calculate position relative to the button
        let top = rect.bottom + 8; // Start 8px below the button
        let left = rect.left;
        
        // Check if there's enough space below the button
        const spaceBelow = viewportHeight - rect.bottom;
        const spaceAbove = rect.top;
        
        // If not enough space below, position above the button
        if (spaceBelow < selectorHeight && spaceAbove > selectorHeight) {
          top = rect.top - selectorHeight - 8;
        }
        
        // Adjust horizontal position to stay within viewport
        if (left + selectorWidth > viewportWidth) {
          left = viewportWidth - selectorWidth - 16;
        }
        
        // Ensure it doesn't go off the left edge
        if (left < 16) {
          left = 16;
        }
        
        // If it still doesn't fit vertically, position it at the top with some margin
        if (top < 16) {
          top = 16;
        }
        
        return { top, left };
      };
      
      // Use requestAnimationFrame to ensure DOM is fully updated
      const updatePosition = () => {
        setPosition(calculatePosition());
      };
      
      requestAnimationFrame(updatePosition);
    }
  }, [open, anchorEl]);

  const handleAddFilter = () => {
    if (!selectedColumn) return;

    const newFilter: FilterConfig = {
      column: selectedColumn,
      type: filterType,
      value: textValue,
      startDate: filterType === 'date' ? startDate : undefined,
      endDate: filterType === 'date' ? endDate : undefined,
    };

    const updatedFilters = [...filters, newFilter];
    setFilters(updatedFilters);
    onFiltersChange(updatedFilters);

    // Reset form
    setSelectedColumn('');
    setTextValue('');
    setStartDate('');
    setEndDate('');
  };

  const handleRemoveFilter = (index: number) => {
    const updatedFilters = filters.filter((_, i) => i !== index);
    setFilters(updatedFilters);
    onFiltersChange(updatedFilters);
  };

  const handleClearAll = () => {
    setFilters([]);
    onFiltersChange([]);
  };

  const handleColumnChange = (columnKey: string) => {
    setSelectedColumn(columnKey);
    // Auto-detect filter type based on column
    if (columnKey === 'timestamp') {
      setFilterType('date');
    } else {
      setFilterType('text');
    }
  };


  if (!open) return null;

  return (
    <Portal>
      <Paper
        ref={selectorRef}
        elevation={8}
        sx={{
          position: 'fixed',
          top: position.top,
          left: position.left,
          width: 320,
          maxHeight: 400,
          zIndex: 1300,
          backgroundColor: 'white',
          borderRadius: 1,
          overflow: 'hidden',
          boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
        }}
      >
        {/* Header */}
        <Box sx={{ 
          p: 2, 
          borderBottom: '1px solid rgba(0,0,0,0.12)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <Typography
            sx={{
              fontSize: '16px',
              fontFamily: 'Montserrat',
              color: 'rgba(0,0,0,0.87)',
              fontWeight: 500,
              lineHeight: '24px',
            }}
          >
            Filters
          </Typography>
          <Button
            size="small"
            onClick={onClose}
            sx={{
              minWidth: 'auto',
              padding: '4px',
              color: 'rgba(0,0,0,0.6)',
              '&:hover': {
                backgroundColor: 'rgba(0,0,0,0.04)',
              },
            }}
          >
            <CloseIcon sx={{ fontSize: 18 }} />
          </Button>
        </Box>

        {/* Filter Form */}
        <Box sx={{ p: 2, borderBottom: '1px solid rgba(0,0,0,0.12)' }}>
          <Box sx={{ mb: 2 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Column</InputLabel>
              <Select
                value={selectedColumn}
                onChange={(e) => handleColumnChange(e.target.value)}
                label="Column"
                sx={{
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(0,0,0,0.23)',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(0,0,0,0.87)',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#337A5C',
                  },
                }}
              >
                {columns.map((column) => (
                  <MenuItem key={column.key} value={column.key}>
                    {column.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {selectedColumn && (
            <>
              {filterType === 'date' ? (
                <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                  <TextField
                    label="Start Date"
                    type="date"
                    size="small"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    sx={{
                      flex: 1,
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgba(0,0,0,0.23)',
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgba(0,0,0,0.87)',
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#337A5C',
                      },
                    }}
                  />
                  <TextField
                    label="End Date"
                    type="date"
                    size="small"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    sx={{
                      flex: 1,
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgba(0,0,0,0.23)',
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgba(0,0,0,0.87)',
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#337A5C',
                      },
                    }}
                  />
                </Box>
              ) : (
                <Box sx={{ mb: 2 }}>
                  <TextField
                    fullWidth
                    label="Contains"
                    size="small"
                    value={textValue}
                    onChange={(e) => setTextValue(e.target.value)}
                    placeholder="Enter text to filter..."
                    sx={{
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgba(0,0,0,0.23)',
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgba(0,0,0,0.87)',
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#337A5C',
                      },
                    }}
                  />
                </Box>
              )}

              <Button
                fullWidth
                variant="contained"
                onClick={handleAddFilter}
                disabled={
                  !selectedColumn ||
                  (filterType === 'date' ? !startDate || !endDate : !textValue.trim())
                }
                sx={{
                  backgroundColor: '#337A5C',
                  color: 'white',
                  '&:hover': {
                    backgroundColor: '#2a5f4a',
                  },
                  '&:disabled': {
                    backgroundColor: 'rgba(0,0,0,0.12)',
                    color: 'rgba(0,0,0,0.26)',
                  },
                }}
              >
                Add Filter
              </Button>
            </>
          )}
        </Box>

        {/* Active Filters */}
        <Box sx={{ maxHeight: 200, overflowY: 'auto' }}>
          {filters.map((filter, index) => (
            <Box
              key={index}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                px: 2,
                py: 1.5,
                borderBottom: '1px solid rgba(0,0,0,0.12)',
                '&:last-child': {
                  borderBottom: 'none',
                },
              }}
            >
              <Box>
                <Typography
                  sx={{
                    fontSize: '14px',
                    fontFamily: 'Montserrat',
                    color: 'rgba(0,0,0,0.87)',
                    fontWeight: 500,
                  }}
                >
                  {columns.find(col => col.key === filter.column)?.label}
                </Typography>
                <Typography
                  sx={{
                    fontSize: '12px',
                    fontFamily: 'Montserrat',
                    color: 'rgba(0,0,0,0.6)',
                  }}
                >
                  {filter.type === 'date'
                    ? `${filter.startDate} - ${filter.endDate}`
                    : `Contains: "${filter.value}"`}
                </Typography>
              </Box>
              <Button
                size="small"
                onClick={() => handleRemoveFilter(index)}
                sx={{
                  minWidth: 'auto',
                  padding: '4px',
                  color: 'rgba(0,0,0,0.6)',
                }}
              >
                <ClearIcon sx={{ fontSize: 16 }} />
              </Button>
            </Box>
          ))}
        </Box>

        {/* Divider */}
        <Divider />

        {/* Footer Controls */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            px: 2,
            py: 1.5,
          }}
        >
          <Button
            onClick={handleClearAll}
            disabled={filters.length === 0}
            sx={{
              color: 'rgba(0,0,0,0.6)',
              fontSize: '12px',
              fontFamily: 'Montserrat',
              fontWeight: 500,
              textTransform: 'none',
              minWidth: 'auto',
              padding: '4px 8px',
              '&:hover': {
                backgroundColor: 'rgba(0,0,0,0.04)',
              },
              '&:disabled': {
                color: 'rgba(0,0,0,0.26)',
              },
            }}
          >
            Clear All
          </Button>
          <Typography
            sx={{
              fontSize: '12px',
              fontFamily: 'Montserrat',
              color: 'rgba(0,0,0,0.6)',
            }}
          >
            {filters.length} filter{filters.length !== 1 ? 's' : ''} applied
          </Typography>
        </Box>
      </Paper>
    </Portal>
  );
};

export default FilterSelector;
